// frontend/src/pages/CodeDetailStats.jsx
import React from "react";
import PropTypes from "prop-types";
import { format, parseISO } from "date-fns";
import ScansByDayChart from "../components/charts/ScansByDayChart";
import DeviceBreakdown from "../components/charts/DeviceBreakdown";
import CountryBars from "../components/charts/CountryBars";

/**
 * StatCard — a single labelled metric tile.
 *
 * @param {{ label: string, value: number|string }} props
 */
function StatCard({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-2xl font-bold text-gray-900">
        {value.toLocaleString()}
      </span>
    </div>
  );
}
StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

/** ScanSources — table of scan origins grouped by anonymised IP network. */
function ScanSources({ sources }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h2 className="text-sm font-semibold text-gray-700">Scan locations</h2>
      <p className="text-xs text-gray-400 mt-0.5 mb-3">
        Approximate. IP addresses are anonymised (host part removed) — only the
        network is stored — and geolocation is best-effort, so these may be
        inaccurate.
      </p>
      {sources.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">
          No location data yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">
                <th className="py-2 pr-4">Network</th>
                <th className="py-2 pr-4">Country</th>
                <th className="py-2 pr-4 text-right">Scans</th>
                <th className="py-2">Last seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sources.map((s) => (
                <tr key={s.ip_subnet}>
                  <td className="py-2 pr-4 font-mono text-xs text-gray-700">
                    {s.ip_subnet}
                  </td>
                  <td className="py-2 pr-4 text-gray-600">
                    {s.country_code ?? "—"}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums text-gray-700">
                    {s.count.toLocaleString()}
                  </td>
                  <td className="py-2 text-gray-500 text-xs whitespace-nowrap">
                    {s.last_scanned_at
                      ? format(parseISO(s.last_scanned_at), "d MMM yyyy, HH:mm")
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
ScanSources.propTypes = {
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      ip_subnet: PropTypes.string,
      country_code: PropTypes.string,
      count: PropTypes.number,
      last_scanned_at: PropTypes.string,
    }),
  ).isRequired,
};

/**
 * CodeDetailStats — KPI cards + the dashboard chart set + scan-source list,
 * scoped to one code.
 *
 * Consumes the per-code analytics bundle from
 * ``GET /api/codes/{code}/analytics/summary``.
 *
 * @param {{ analytics: object }} props
 */
function CodeDetailStats({ analytics, code }) {
  return (
    <div className="space-y-4">
      {/* Period KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Today" value={analytics.scans_today} />
        <StatCard label="This week" value={analytics.scans_this_week} />
        <StatCard label="This month" value={analytics.scans_this_month} />
        <StatCard label="This year" value={analytics.scans_this_year} />
        <StatCard label="All time" value={analytics.total_scans} />
        <StatCard
          label="Unique visitors"
          value={analytics.unique_ips_last_30_days}
        />
      </div>

      {/* Scans by day */}
      <ScansByDayChart data={analytics.scans_by_day ?? []} height={200} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <DeviceBreakdown code={code} />
        <CountryBars data={analytics.scans_by_country ?? []} />
      </div>

      {/* Scan origins (anonymised IP network + geo) */}
      <ScanSources sources={analytics.sources ?? []} />
    </div>
  );
}

CodeDetailStats.propTypes = {
  analytics: PropTypes.shape({
    total_scans: PropTypes.number.isRequired,
    scans_today: PropTypes.number.isRequired,
    scans_this_week: PropTypes.number.isRequired,
    scans_this_month: PropTypes.number.isRequired,
    scans_this_year: PropTypes.number.isRequired,
    unique_ips_last_30_days: PropTypes.number.isRequired,
    scans_by_day: PropTypes.array,
    scans_by_country: PropTypes.array,
    scans_by_device: PropTypes.array,
    sources: PropTypes.array,
  }).isRequired,
  code: PropTypes.string.isRequired,
};

export default CodeDetailStats;
